import React, { useEffect, useRef } from 'react';
import {
  Circle,
  Check,
  CircleDot,
  XCircle,
  Clock,
  ArrowRight,
  SkipForward,
} from 'lucide-react';
import type { PlanStep, OrchestratorStepStatus } from '../../../types/smartStart';

interface Props {
  plan: PlanStep[];
  activeIndex: number;
  onSelectStep: (index: number) => void;
}

const STATUS_META: Record<OrchestratorStepStatus, { label: string; icon: React.ElementType; color: string; ring: string }> = {
  pending: { label: 'Pending', icon: Circle, color: 'text-gray-400', ring: 'ring-gray-200' },
  ready: { label: 'Ready', icon: ArrowRight, color: 'text-lavender', ring: 'ring-lavender/40' },
  in_progress: { label: 'In progress', icon: CircleDot, color: 'text-blue-600', ring: 'ring-blue-300' },
  completed: { label: 'Completed', icon: Check, color: 'text-emerald-600', ring: 'ring-emerald-300' },
  rejected: { label: 'Rejected', icon: XCircle, color: 'text-red-600', ring: 'ring-red-300' },
  skipped: { label: 'Skipped', icon: SkipForward, color: 'text-gray-400', ring: 'ring-gray-200' },
  abandoned: { label: 'Abandoned', icon: Clock, color: 'text-amber-600', ring: 'ring-amber-300' },
};

const TARGET_SCREEN_LABELS: Record<string, string> = {
  sop_builder: 'SOP Builder',
  ai_council: 'AI Council',
  process_maps: 'Process Maps',
  interviews: 'Interviews',
  risk_mapping: 'Risk Mapping',
  rcsa_process_steps: 'RCSA Process Steps',
  process_optimization: 'Process Optimization',
  bd_engagements: 'BD Pipeline',
  bd_engagement_detail: 'BD Pipeline',
  smart_data_analyst: 'Smart Data Analyst',
  data_cleaning: 'Data Cleaning',
  project_management: 'Project Management',
  intelligence: 'Jarvis Intelligence',
  inline_export: 'Export',
  inline_review: 'Review',
};

const SUB_STEP_LABELS: Record<string, string> = {
  intake: 'Intake',
  synthesis: 'Synthesis',
  ou_oa: 'OU/OA deck',
  plan: 'Project plan',
  datasim: 'DataSim',
  sow: 'SOW',
  won: 'PM handoff',
  'review-summaries': 'Review summaries',
  'approve-stakeholders': 'Approve stakeholders',
  'validate-process-map': 'Validate process map',
  'generate-sop': 'Generate SOP',
  'approve-terminology': 'Approve terminology',
  'fill-gaps': 'Fill gaps',
  'update-change': 'Update / change',
};

function humaniseSubStep(s?: string | null): string {
  if (!s) return '';
  return SUB_STEP_LABELS[s] || s.replace(/[-_]/g, ' ');
}

const ProgressTracker: React.FC<Props> = ({ plan, activeIndex, onSelectStep }) => {
  const total = plan.length;
  const completed = plan.filter((p) => p.status === 'completed' || p.status === 'skipped').length;
  const percent = total > 0 ? Math.round((completed / total) * 100) : 0;

  // Smoothly scroll the active step into view whenever it changes — keeps the
  // user oriented when auto-advance moves focus to a later step they can't see.
  const activeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    activeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [activeIndex]);

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs uppercase tracking-wide text-gray-500 font-semibold">
          Your plan
        </div>
        <div className="text-xs text-gray-600">
          {completed} / {total} done
        </div>
      </div>

      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden mb-4">
        <div
          className="h-full bg-lavender transition-all"
          style={{ width: `${percent}%` }}
        />
      </div>

      <ol className="space-y-1">
        {plan.map((step, idx) => {
          const meta = STATUS_META[step.status] || STATUS_META.pending;
          const Icon = meta.icon;
          const isActive = idx === activeIndex;
          const screenLabel = TARGET_SCREEN_LABELS[step.target_screen] || step.target_screen;
          return (
            <li key={`${step.step_key}-${idx}`}>
              <button
                ref={isActive ? activeRef : undefined}
                onClick={() => onSelectStep(idx)}
                className={`w-full text-left flex items-start gap-3 p-2.5 rounded-lg transition ${
                  isActive
                    ? 'bg-lavender/5 ring-1 ring-inset ring-lavender/30'
                    : 'hover:bg-gray-50'
                }`}
              >
                <div className={`mt-0.5 w-6 h-6 rounded-full bg-white ring-2 ${meta.ring} flex items-center justify-center`}>
                  <Icon className={`w-3.5 h-3.5 ${meta.color}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-gray-900 truncate">
                    {idx + 1}. {step.label}
                  </div>
                  <div className="text-[11px] text-gray-500 truncate">
                    {screenLabel}
                    {step.sub_step ? ` · ${humaniseSubStep(step.sub_step)}` : ''}
                  </div>
                  {step.missing_slots && step.missing_slots.length > 0 && step.status === 'pending' && (
                    <div className="text-[10px] text-amber-600 mt-0.5">
                      Needs: {step.missing_slots.join(', ')}
                    </div>
                  )}
                </div>
              </button>
            </li>
          );
        })}
      </ol>
    </div>
  );
};

export default ProgressTracker;
